import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, LogOut, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';

export default function TermsConditionsPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user, logout, openLogin } = useAuth();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const currentYear = new Date().getFullYear();

  const theme = {
    bg: isDark ? '#0f172a' : '#f3f4f6',
    panelBg: isDark ? 'rgba(12,10,8,0.86)' : 'rgba(255,255,255,0.9)',
    border: isDark ? 'rgba(148,163,184,0.22)' : '#e2e8f0',
    textPrimary: isDark ? 'rgba(255,255,255,0.92)' : '#111827',
    textSecondary: isDark ? 'rgba(255,255,255,0.72)' : '#4b5563',
    textMuted: isDark ? 'rgba(255,255,255,0.56)' : '#9ca3af',
    accent: isDark ? '#F59E0B' : '#2563eb',
  };

  useEffect(() => {
    const fn = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setIsProfileMenuOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  return (
    <div className={isDark ? 'dark-app' : 'light-app'} style={{ fontFamily: "'Manrope', sans-serif", background: theme.bg, color: theme.textPrimary, minHeight: '100vh' }}>
      <header
        className="nav-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: theme.panelBg,
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${theme.border}`,
          padding: '10px 24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = theme.textPrimary}
            onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
          >
            <ArrowLeft size={14} />
            Back
          </button>
          <div style={{ width: 1, height: 18, background: theme.border }} />
          <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', borderRadius: '8px', padding: '4px', border: isDark ? 'none' : '1px solid #e2e8f0' }}>
              <img src="/assets/logo.png" alt="fairhire logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span style={{ fontSize: 18, fontWeight: 800, color: theme.textPrimary, fontFamily: "'Playfair Display', serif" }}>fairhire</span>
          </div>
        </div>

        <div style={{ position: 'relative' }} ref={profileMenuRef}>
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ThemeToggle />
              <div style={{ position: 'relative' }}>
                <button
                  data-testid="profile-icon"
                  onClick={() => setIsProfileMenuOpen(v => !v)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.1)',
                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(37,99,235,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <UserIcon size={16} color={isDark ? 'rgba(255,255,255,0.8)' : '#2563eb'} />
                </button>
                <AnimatePresence>
                  {isProfileMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 8px)',
                        width: 200,
                        background: isDark ? 'rgba(13,16,25,0.98)' : '#ffffff',
                        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #dbe7fb',
                        borderRadius: 14,
                        padding: 8,
                        boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 12px 36px rgba(37,99,235,0.18)',
                      }}
                    >
                      <div style={{ padding: '8px 12px', borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', marginBottom: 4 }}>
                        <p style={{ fontSize: 11, color: theme.textMuted, marginBottom: 2 }}>Signed in as</p>
                        <p style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</p>
                      </div>
                      <button
                        onClick={() => { setIsProfileMenuOpen(false); navigate('/profile'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none', color: theme.textSecondary, fontSize: 13, cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <UserIcon size={14} />
                        Profile
                      </button>
                      <button
                        onClick={() => { logout(); setIsProfileMenuOpen(false); navigate('/'); toast.success('Logged out'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none', color: '#f87171', fontSize: 13, cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <LogOut size={14} />
                        Logout
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ThemeToggle />
              <button
                data-testid="login-button"
                onClick={() => openLogin()}
                style={{ padding: '8px 18px', borderRadius: 100, background: isDark ? 'rgba(255,255,255,0.08)' : '#f8fafc', border: `1px solid ${theme.border}`, color: theme.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.14)' : '#f1f5f9'; e.currentTarget.style.color = theme.textPrimary; }}
                onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : '#f8fafc'; e.currentTarget.style.color = theme.textSecondary; }}
              >
                Sign in
              </button>
            </div>
          )}
        </div>
      </header>

      <main style={{ maxWidth: 1120, margin: '0 auto', padding: '132px 24px 72px' }}>
        <article style={{ maxWidth: 760, margin: '0 auto', padding: 0, fontFamily: "'Source Serif 4', 'Merriweather', Georgia, 'Times New Roman', serif" }}>
          <h1 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", fontSize: 'clamp(44px, 5.2vw, 56px)', lineHeight: 1.06, margin: 0, color: theme.textPrimary, fontWeight: 800, letterSpacing: '-0.02em' }}>Terms & Conditions</h1>
          <p style={{ marginTop: 18, fontSize: 20, color: theme.textMuted, fontWeight: 600 }}>Last updated: May 2026</p>

          <p style={{ marginTop: 60, fontSize: 18, lineHeight: 1.75, color: theme.textSecondary, fontWeight: 500 }}>
            Welcome to FairHire.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            By accessing or using FairHire, you agree to comply with and be bound by these Terms & Conditions. If you do not agree with any part of these terms, please do not use the platform.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Use of Platform</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            FairHire helps job seekers discover relevant companies, hiring patterns, referrals, and job opportunities using publicly available and user-provided information.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            You agree to use the platform only for lawful purposes and in a manner that does not harm, misuse, or disrupt the platform or other users.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>User Responsibility</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>You are responsible for:</p>
          <ul style={{ marginTop: 16, marginBottom: 0, paddingLeft: 36, listStyleType: 'disc', color: theme.textSecondary, fontSize: 18, lineHeight: 1.7 }}>
            <li style={{ marginBottom: 8 }}>The accuracy of information you provide</li>
            <li style={{ marginBottom: 8 }}>Maintaining the confidentiality of your account</li>
            <li>Any activity performed using your account</li>
          </ul>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            FairHire is not responsible for decisions made based on insights, recommendations, or data shown on the platform.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Payments & Subscription</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            Certain features may require payment or subscription.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            By making a payment, you authorise FairHire and its payment partners to process the transaction securely.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            All pricing is subject to change at any time.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Refunds & Cancellation</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            Please refer to our Refund & Cancellation Policy for details regarding refunds, cancellations, and subscription management.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Intellectual Property</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            All platform content including branding, design, software, and platform logic belongs to FairHire unless otherwise stated.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            You may not copy, distribute, reverse engineer, or misuse any part of the platform without permission.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Platform Availability</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            We continuously improve FairHire, so some features may change, be modified, or be removed over time.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            We do not guarantee uninterrupted availability of the platform.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Limitation of Liability</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            FairHire is provided on an &quot;as is&quot; basis.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            We are not liable for:
          </p>
          <ul style={{ marginTop: 16, marginBottom: 0, paddingLeft: 36, listStyleType: 'disc', color: theme.textSecondary, fontSize: 18, lineHeight: 1.7 }}>
            <li style={{ marginBottom: 8 }}>Job outcomes</li>
            <li style={{ marginBottom: 8 }}>Hiring decisions</li>
            <li>Referral outcomes</li>
          </ul>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Termination</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            We reserve the right to suspend or terminate accounts involved in misuse, abuse, spam, fraudulent activity, or violation of these terms.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Changes to Terms</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            These Terms may be updated from time to time. Continued use of FairHire after updates means you accept the revised terms.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Contact</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginBottom: 8, marginTop: 0 }}>For any questions or support requests, contact:</p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            Email:{' '}
            <a href="mailto:support@fairhire.in" style={{ color: isDark ? '#93c5fd' : '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
              support@fairhire.in
            </a>
          </p>
        </article>
      </main>

      <footer style={{ padding: '22px 24px', borderTop: `1px solid ${theme.border}`, background: theme.bg }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 13, color: theme.textMuted, margin: 0 }}>© {currentYear} fairhire. All rights reserved.</p>
          <a href="mailto:support@fairhire.in" style={{ fontSize: 13, color: theme.textSecondary, textDecoration: 'none', fontWeight: 500 }}>support@fairhire.in</a>
        </div>
      </footer>
    </div>
  );
}
